import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useRef,
  useCallback,
} from "react";
import { View, Text, TouchableOpacity, Image } from "react-native";
import { getLoginData } from "../utility/AppStoreData";
import {
  getUnseenNotificationCounts,
  markNotificationAsSeen,
} from "../services/notification";
import { ChallengeActionPopup } from "../screens/Auth/WebScreens/PostSignupPopups/ChallengeActionPopup";
import {
  connectWebSocket,
  disconnectWebSocket,
  sendMessage,
  sendEventMessage,
} from "../utility/notification/NotificationWebSocketUtils";
import { toast } from "react-toastify";
import { ACTION_TYPES } from "../utility/notification/notificationConfig";
import { fetchStreamParticipantDetails } from "../services/stream";
import { useLiveStream } from "./LiveStreamContext";
import { StackActions, useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../utility/NavigationHelper";
import { AppScreens } from "../screens/Utility/AppScreens";
import { BACKGROUND_NOTIFICATION_TYPES } from "../config/notificationConfig";
import { fetchEventDetails } from "../services/eventsAPI";
import NotificationDetails from "../screens/Auth/WebScreens/Notifications/NotificationPopup/NotificationDetails";
import { MobileNotificationDetails } from "../screens/Auth/WebScreens/Notifications/NotificationPopup/MobileNotificationDetails";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import CustomCoinToast from "../Components/CustomCoinToast";
import EventsDetailsUpdatedModal from "../screens/Auth/Event/UpdateEventsPopup/EventsDetailsUpdatedModal";
import ChangesApprovedModal from "../screens/Auth/Event/UpdateEventsPopup/ChangesApprovedModal";
import ChangesRejectedModal from "../screens/Auth/Event/UpdateEventsPopup/ChangesRejectedModal";
import GreatEffortModal from "../Components/stream-setup-components/GreatEffortModal";
import WinnerModal from "../screens/Auth/WebScreens/Modals/WinnerModal";
import FinalWarning from "../screens/Auth/WebScreens/SaleBeltScreens/FinalWarning";
import ChallengeNotificationPopup from "../Components/live-streaming-common-components/ChallengeNotificationPopup";
import ChallengeResponsePopup from "../Components/live-streaming-common-components/ChallengeResponsePopup";
import { AppColors } from "../utility/AppColors";
import { AppFontStyle } from "../styles/AppFontStyle";
import { AppImages } from "../utility/AppImages";
import { LinearGradient } from "expo-linear-gradient";

interface Notification {
  status: string;
  type: string;
  data: any;
  notificationData?: any;
  actionType?: string;
  category?: string;
  userId?: string;
  notificationId?: string;
  id?: string;
}

interface EventNotification {
  eventID: string;
  type?: string;
}

interface LoginData {
  username: string;
  AccessToken: string;
  IdToken: string;
  idTokenExpiresIn: number;
  accessTokenExpiresIn: number;
}

interface NotificationContextProps {
  notifications: Notification[];
  eventNotifications: any[];
  newNotification: {
    type: string;
    data: any;
    userId: string;
    senderId: string;
    actionType: string;
    actionStatus: string;
  };
  updateNotification: {
    response: string;
    challengeId: string;
  };
  sendNotification: (senderId: string, notificationId: string) => Promise<void>;
  sendEventNotification: (eventNotification: any) => Promise<void>;
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  setUpdateNotification: any;
  showModal: boolean;
  newNotificationCount: number;
  setNewNotificationCount: React.Dispatch<React.SetStateAction<any>>;
  eventStatus: any;
  activityUpdate: any;
  signUpUser: any;
  checkInStatus: CheckInStatus;
  checkInNotifications: NotificationData[];
  refreshNotificationCounts: () => Promise<void>;
  notificationCounts: {
    organizer: number;
    challenger: number;
    audience: number;
    total: number;
  };
  isOnNotificationScreen: boolean;
  visitedNotificationTabs: Set<string>;
  trackTabVisit: (tabName: string) => void;
  markNotificationsAsSeen: () => Promise<void>;
  showChallengeActionPopup: (
    action: "accept" | "reject" | "negotiate",
    autoRedirect?: boolean
  ) => void;
  hideChallengeActionPopup: () => void;
  challengeActionPopupState: {
    visible: boolean;
    action: "accept" | "reject" | "negotiate";
    autoRedirect: boolean;
  };
}

interface NotificationProviderProps {
  children: ReactNode;
}

interface CheckInStatus {
  organiser: boolean;
  challenger: boolean;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(
  undefined
);

interface NotificationData {
  userId: string;
  eventId: string;
  type: string;
  message: string;
  actionType: string;
  actionStatus: string;
  notificationId: string;
  createdAt: string;
  isRead: number;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [eventNotifications, setEventNotifications] = useState([]);
  const [currentNotification, setCurrentNotification] = useState<any | null>(
    null
  );
  const [signUpUser, setSignUpUser] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showSmallModalForNewChallenge, setShowSmallModalForNewChallenge] =
    useState(false);
  const [showChallengeResponsePopup, setShowChallengeResponsePopup] =
    useState(false);
  const [challengeResponseNotification, setChallengeResponseNotification] =
    useState<any>(null);
  const [showCancelledEventPopup, setShowCancelledEventPopup] = useState(false);
  const [cancelledEventNotification, setCancelledEventNotification] =
    useState<any>(null);
  const [showInfluencerApprovalPopup, setShowInfluencerApprovalPopup] = useState(false);
  const [influencerApprovalData, setInfluencerApprovalData] = useState<any>(null);
  const [showNegotiationActivityPopup, setShowNegotiationActivityPopup] = useState(false);
  const [negotiationActivityData, setNegotiationActivityData] = useState<any>(null);
  const [newNotification, setNotification] = useState<any>({});
  const [loginUserData, setLoginUserData] = useState<LoginData | null>(null);
  const loginUserDataRef = useRef<LoginData | null>(null);
  const [showCustomCoinToast, setShowCustomCoinToast] = useState(false);
  const [customCoinToastText, setCustomCoinToastText] = useState("");
  const [customCoinToastDay, setCustomCoinToastDay] = useState(0);
  const [customCoinToastCoin, setCustomCoinToastCoin] = useState(0);
  const [updateNotification, setUpdateNotification] = useState<any>({});
  const [newNotificationCount, setNewNotificationCount] = useState(0);
  const [currentEventUpdate, setCurrentEventUpdate] = useState<any>(null);
  const [previousEventDetails, setPreviousEventDetails] = useState<any>(null);
  const [showEventUpdateModal, setShowEventUpdateModal] = useState(false);
  const [showChangesApprovedModal, setShowChangesApprovedModal] =
    useState(false);
  const [showChangesRejectedModal, setShowChangesRejectedModal] =
    useState(false);
  const [currentEventStatusUpdate, setCurrentEventStatusUpdate] =
    useState<any>(null);
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [greatEffortPopup, setGreatEffortPopup] = useState(false);
  const [finalWarningPopUp, setFinalWarningPopUp] = useState(false);
  const [eventData, setEventData] = useState(null);
  const navigation = useNavigation();
  const [checkInNotifications, setCheckInNotifications] = useState<
    NotificationData[]
  >([]);
  const [checkInStatus, setCheckInStatus] = useState<CheckInStatus>({
    organiser: false,
    challenger: false,
  });
  const [notificationCounts, setNotificationCounts] = useState({
    organizer: 0,
    challenger: 0,
    audience: 0,
    total: 0,
  });
  const [isOnNotificationScreen, setIsOnNotificationScreen] = useState(false);
  const [visitedNotificationTabs, setVisitedNotificationTabs] = useState<
    Set<string>
  >(new Set());
  const [challengeActionPopupState, setChallengeActionPopupState] = useState({
    visible: false,
    action: "reject" as "accept" | "reject" | "negotiate",
    autoRedirect: false,
  });
  const fieldsToCheck = [
    "title",
    "startDateTime",
    "gameTitle",
    "gamerID",
    "device",
    "eventImageUrl",
    "description",
  ];

  const hasRelevantChanges = (updatedDetails: any) => {
    return fieldsToCheck.some(
      (field) =>
        updatedDetails[field] !== undefined && updatedDetails[field] !== null
    );
  };

  const fetchPreviousDetails = async (eventId: string, accessToken: string) => {
    try {
      const response = await fetchEventDetails(eventId, accessToken);
      setPreviousEventDetails(response);
    } catch (error) { }
  };

  const [eventStatus, setEventStatus] = useState<{
    eventId: string;
    status: string;
    chatId: string;
    challengeDetails: any;
  } | null>(null);
  const [activityUpdate, setActivityUpdate] = useState<any>(null);
  const [eventDetails, setEventDetails] = useState<any>(null);
  const [newWinner, setNewWinner] = useState<any>(null);
  const { setOrganizerData } = useLiveStream();

  // Centralized notification counts refresh function
  const refreshNotificationCounts = useCallback(async () => {
    if (!loginUserDataRef.current?.AccessToken) {
      return;
    }

    try {
      const response = await getUnseenNotificationCounts(
        loginUserDataRef.current.AccessToken
      );

      if (response.statusCode === 200) {
        setNotificationCounts(response.body);
      } else {
        setNotificationCounts({
          organizer: 0,
          challenger: 0,
          audience: 0,
          total: 0,
        });
      }
    } catch (error) {
      setNotificationCounts({
        organizer: 0,
        challenger: 0,
        audience: 0,
        total: 0,
      });
    }
  }, []);

  const trackTabVisit = (tabName: string) => {
    setVisitedNotificationTabs((prev) => {
      if (prev.has(tabName)) {
        return prev; // Don't update if tab already visited
      }
      return new Set(prev).add(tabName);
    });
  };

  const getApiCategory = (tabName: string): string => {
    switch (tabName) {
      case "I've Organised":
        return "organiser";
      case "Challenges":
        return "challenger";
      case "Im Watching":
        return "audience";
      default:
        return "";
    }
  };

  const markNotificationsAsSeen = async () => {
    if (!loginUserDataRef.current?.AccessToken) {
      return;
    }

    if (visitedNotificationTabs.size === 0) {
      return;
    }

    try {
      const visitedCategories = Array.from(visitedNotificationTabs)
        .map(getApiCategory)
        .filter((category) => category !== "");

      await Promise.all(
        visitedCategories.map((category) =>
          markNotificationAsSeen(
            loginUserDataRef.current!.AccessToken,
            category
          )
        )
      );

      setVisitedNotificationTabs(new Set());
      await refreshNotificationCounts();
    } catch (error) {
      console.error("Error marking notifications as seen:", error);
    }
  };

  const showChallengeActionPopup = (
    action: "accept" | "reject" | "negotiate",
    autoRedirect: boolean = false
  ) => {
    setChallengeActionPopupState({
      visible: true,
      action,
      autoRedirect,
    });
  };

  const hideChallengeActionPopup = () => {
    setChallengeActionPopupState({
      visible: false,
      action: "reject",
      autoRedirect: false,
    });
  };

  useEffect(() => {
    if (loginUserData?.AccessToken) {
      refreshNotificationCounts();
    }
  }, [loginUserData, refreshNotificationCounts]);

  useEffect(() => {
    const checkNotificationScreen = () => {
      const currentUrl = window.location.href;
      const onNotificationScreen = currentUrl.includes("/notifications");

      setIsOnNotificationScreen((prevState) => {
        if (prevState !== onNotificationScreen) {
          // REMOVED: Auto-marking notifications as seen when leaving screen
          // if (prevState && !onNotificationScreen) {
          //   // User is leaving notification screen - mark notifications as seen
          //   markNotificationsAsSeen();
          // }
          if (!prevState && onNotificationScreen) {
            // User is entering notification screen - refresh counts
            refreshNotificationCounts();
          }
          return onNotificationScreen;
        }
        return prevState;
      });
    };

    checkNotificationScreen();
    const interval = setInterval(checkNotificationScreen, 1000);

    return () => clearInterval(interval);
  }, [refreshNotificationCounts]);

  const notificationHandlers = {
    check_in: (notification: Notification) => {
      setCheckInNotifications((prev) => [...prev, notification.data]);
      toast.info(notification.data.message);
      if (notification.data.actionType === "organiser_check_in") {
        setCheckInStatus((prev) => ({ ...prev, organiser: true }));
      } else if (notification.data.actionType === "challenger_check_in") {
        setCheckInStatus((prev) => ({ ...prev, challenger: true }));
      }
    },

    check_in_update: (notification: Notification) => {
      setCheckInNotifications((prev) => [...prev, notification.data]);
      toast.info(notification.data.message);

      if (notification.data.actionType === "organiser_check_in") {
        setCheckInStatus((prev) => ({ ...prev, organiser: true }));
      } else if (notification.data.actionType === "challenger_check_in") {
        setCheckInStatus((prev) => ({ ...prev, challenger: true }));
      }
    },

    update: (notification: Notification) => {
      setUpdateNotification(notification.data);
      if (notification.data.response === true) {
        toast.success("Your Challenge has been Accepted");
      } else if (notification.data.response === false) {
        toast.info("Your Challenge has been Rejected, change Challenger");
      }
    },

    "user-registered": (notification: Notification) => {
      const { eventID, userData } = notification.data || {};
      if (!eventID || !userData) {
        return;
      }
      setSignUpUser((prev) => [
        ...prev,
        { type: notification.type, eventID, data: userData },
      ]);
    },

    event_status_update: (notification: Notification) => {
      setEventStatus(notification.data);
    },

    event_update: async (notification: Notification) => {
      const { eventId, updatedEventDetails } = notification.data;
      if (hasRelevantChanges(updatedEventDetails)) {
        const accessToken = loginUserDataRef.current?.AccessToken;
        if (accessToken) {
          await fetchPreviousDetails(eventId, accessToken);
          setCurrentEventUpdate(notification.data);
          setShowEventUpdateModal(true);
        }
      }
    },

    event_update_status: (notification: Notification) => {
      const { eventId, actionStatus } = notification.data;
      setCurrentEventStatusUpdate(notification.data);
      if (actionStatus === "accepted") {
        setShowChangesApprovedModal(true);
      } else if (actionStatus === "rejected") {
        setShowChangesRejectedModal(true);
      }
    },

    coins_reward: (notification: Notification) => {
      setShowCustomCoinToast(true);
      setCustomCoinToastText(notification.data.message);
      setCustomCoinToastCoin(notification.data.coinsEarned);
      setCustomCoinToastDay(1);
    },

    "event-reminder": (notification: Notification) => {
      toast.info(notification.data.message);
    },

    "check-in": (notification: Notification) => {
      toast.info(notification.data.message);
      if (notification.data.category === "half hour check-in") {
        setEventData(notification.data);
        setFinalWarningPopUp(true);
      }
    },

    "check-in-missed": (notification: Notification) => {
      toast.info(notification.data.message);
    },

    completed: (notification: Notification) => {
      toast.info(notification.data.message);
    },

    cancelled: (notification: Notification) => {
      // Show cancelled event popup for live notification
      setCancelledEventNotification(notification);
      setShowCancelledEventPopup(true);
    },

    // Handle cancelled event by actionType
    checkin_failed: (notification: Notification) => {
      setCancelledEventNotification(notification);
      setShowCancelledEventPopup(true);
    },

    no_challenger_selected: (notification: Notification) => {
      setCancelledEventNotification(notification);
      setShowCancelledEventPopup(true);
    },

    no_response: (notification: Notification) => {
      setCancelledEventNotification(notification);
      setShowCancelledEventPopup(true);
    },

    user_activity_update: (notification: Notification) => {
      // Set the activity update so components can react to it
      setActivityUpdate(notification.data);

      // Show popup for negotiation-related activities
      const activityType = notification.data?.activityType;
      if (activityType === 'negotiation_started' || activityType === 'terms_modified') {
        // Show bottom toast popup for negotiation (same style as Challenge Declined)
        setNegotiationActivityData(notification.data);
        setShowNegotiationActivityPopup(true);
      }
    },

    // Handle account-related notifications (influencer approval/rejection)
    account_update: (notification: Notification) => {
      const { type, title, message, referralCode } = notification.data || {};
      console.log("Account update notification received:", notification.data);

      if (type === "influencer_approved" || type === "influencer_rejected") {
        setInfluencerApprovalData({
          type,
          title,
          message,
          referralCode,
          isApproved: type === "influencer_approved",
        });
        setShowInfluencerApprovalPopup(true);
      }
    },
  };

  const handleNotification = async (notification: Notification) => {
    if (!notification) {
      return;
    }

    const notificationId =
      notification.notificationData?.notificationId ||
      notification.data?.notificationId ||
      notification.data?.id ||
      notification.id;

    setNotifications((prev) => {
      const existingNotifications = prev || [];

      if (notificationId) {
        const exists = existingNotifications.some((existingNotification) => {
          const existingId =
            existingNotification.notificationData?.notificationId ||
            existingNotification.data?.notificationId ||
            existingNotification.data?.id ||
            existingNotification.id;
          return existingId === notificationId;
        });

        if (exists) {
          return prev;
        }
      }

      const notificationWithCategory = {
        ...notification,
        data: {
          ...notification.data,
          category: notification.data?.category || notification.category,
        },
      };

      return [...existingNotifications, notificationWithCategory];
    });

    setCurrentNotification(notification);
    setNotification(notification);

    const shouldIncrementCount = !BACKGROUND_NOTIFICATION_TYPES.includes(
      notification.type
    );
    if (shouldIncrementCount) {
      setNewNotificationCount((prevCount) => prevCount + 1);
      // Refresh the notification counts after a slight delay to ensure backend is updated
      setTimeout(() => {
        refreshNotificationCounts();
      }, 500);
    }

    if (notification.actionType === ACTION_TYPES.accept_reject) {
      setShowSmallModalForNewChallenge(true);
    }

    // Handle challenge response notifications (accepted/rejected)
    if (notification.actionType === ACTION_TYPES.challenge_response) {
      setChallengeResponseNotification(notification);
      setShowChallengeResponsePopup(true);
    }

    // Handle cancelled event notifications
    const cancelledActionTypes = ["checkin_failed", "no_challenger_selected", "no_response"];
    if (cancelledActionTypes.includes(notification.actionType) ||
      cancelledActionTypes.includes(notification.data?.actionType)) {
      setCancelledEventNotification(notification);
      setShowCancelledEventPopup(true);
    }

    const handler =
      notificationHandlers[notification.type] ||
      notificationHandlers[notification.data?.type];
    if (handler) {
      await handler(notification);
    }
  };

  const handleEventNotification = async (eventNotification: any) => {
    if (eventNotification.type === "success") {
      const loginData = await getLoginData();
      const response = await fetchStreamParticipantDetails(
        eventNotification?.data?.eventID,
        loginData?.AccessToken
      );
      setNewWinner(response.body.winnerDetails);
      const winnerDetails = response.body?.winnerDetails
        ? response.body.winnerDetails
        : null;
      setEventDetails(response.body.eventDetails);
      if (winnerDetails?.username === loginData?.username) {
        setShowWinnerModal(true);
      } else {
        setGreatEffortPopup(true);
      }
    } else if (eventNotification.type === "dispute") {
      const loginData = await getLoginData();
      const response = await fetchStreamParticipantDetails(
        eventNotification?.data?.eventID,
        loginData?.AccessToken
      );
      setOrganizerData((prev) => ({
        ...prev,
        eventDetails: response.body.eventDetails,
        disputeDetails: response.body.disputeDetails,
        winnerDetails: eventNotification?.data?.winnerDetails || {},
      }));
    } else {
      setEventNotifications((prev) => [...prev, eventNotification]);
      setNewNotificationCount((prevCount) => prevCount + 1);
      // Refresh the notification counts after a slight delay to ensure backend is updated
      setTimeout(() => {
        refreshNotificationCounts();
      }, 500);
    }
  };

  const autoConnect = async () => {
    const loginData: LoginData | null = await getLoginData();
    if (loginData) {
      setLoginUserData(loginData);
      loginUserDataRef.current = loginData;
      if (loginData.username && loginData.AccessToken) {
        connectWebSocket(
          loginData.username,
          loginData.AccessToken,
          handleNotification,
          handleEventNotification
        );
      }
    }
  };

  const sendNotification = async (senderId: string, notificationId: string) => {
    if (loginUserDataRef.current && loginUserDataRef.current.AccessToken) {
      sendMessage(
        senderId,
        notificationId,
        loginUserDataRef.current.AccessToken
      );
    }
  };

  const sendEventNotification = async (
    eventNotification: EventNotification
  ) => {
    if (loginUserDataRef.current && loginUserDataRef.current.AccessToken) {
      try {
        const enhancedNotification = {
          ...eventNotification,
          accessToken: loginUserDataRef.current.AccessToken,
          userId: loginUserDataRef.current.username,
        };
        sendEventMessage(enhancedNotification);
      } catch (error) { }
    }
  };

  useEffect(() => {
    autoConnect();
    return () => {
      disconnectWebSocket();
    };
  }, []);

  useEffect(() => {
    loginUserDataRef.current = loginUserData;
  }, [loginUserData]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        eventNotifications,
        sendNotification,
        sendEventNotification,
        setNotifications,
        showModal,
        newNotification,
        updateNotification,
        setUpdateNotification,
        newNotificationCount,
        setNewNotificationCount,
        eventStatus,
        activityUpdate,
        signUpUser,
        checkInNotifications,
        checkInStatus,
        refreshNotificationCounts,
        notificationCounts,
        isOnNotificationScreen,
        visitedNotificationTabs,
        trackTabVisit,
        markNotificationsAsSeen,
        showChallengeActionPopup,
        hideChallengeActionPopup,
        challengeActionPopupState,
      }}
    >
      {children}
      <NotificationModals
        currentNotification={currentNotification}
        loginUserData={loginUserData}
        showModal={showModal}
        setShowModal={setShowModal}
        showSmallModalForNewChallenge={showSmallModalForNewChallenge}
        setShowSmallModalForNewChallenge={setShowSmallModalForNewChallenge}
        showCustomCoinToast={showCustomCoinToast}
        setShowCustomCoinToast={setShowCustomCoinToast}
        customCoinToastText={customCoinToastText}
        customCoinToastDay={customCoinToastDay}
        customCoinToastCoin={customCoinToastCoin}
        currentEventUpdate={currentEventUpdate}
        previousEventDetails={previousEventDetails}
        showEventUpdateModal={showEventUpdateModal}
        setShowEventUpdateModal={setShowEventUpdateModal}
        currentEventStatusUpdate={currentEventStatusUpdate}
        showChangesApprovedModal={showChangesApprovedModal}
        setShowChangesApprovedModal={setShowChangesApprovedModal}
        showChangesRejectedModal={showChangesRejectedModal}
        setShowChangesRejectedModal={setShowChangesRejectedModal}
        showWinnerModal={showWinnerModal}
        setShowWinnerModal={setShowWinnerModal}
        eventDetails={eventDetails}
        newWinner={newWinner}
        greatEffortPopup={greatEffortPopup}
        setGreatEffortPopup={setGreatEffortPopup}
        navigation={navigation}
        finalWarningPopUp={finalWarningPopUp}
        setFinalWarningPopUp={setFinalWarningPopUp}
        eventData={eventData}
        loginUserDataRef={loginUserDataRef}
        showChallengeResponsePopup={showChallengeResponsePopup}
        setShowChallengeResponsePopup={setShowChallengeResponsePopup}
        challengeResponseNotification={challengeResponseNotification}
        showCancelledEventPopup={showCancelledEventPopup}
        setShowCancelledEventPopup={setShowCancelledEventPopup}
        cancelledEventNotification={cancelledEventNotification}
        showInfluencerApprovalPopup={showInfluencerApprovalPopup}
        setShowInfluencerApprovalPopup={setShowInfluencerApprovalPopup}
        influencerApprovalData={influencerApprovalData}
        showNegotiationActivityPopup={showNegotiationActivityPopup}
        setShowNegotiationActivityPopup={setShowNegotiationActivityPopup}
        negotiationActivityData={negotiationActivityData}
      />
    </NotificationContext.Provider>
  );
};

const GlobalChallengeActionPopup: React.FC = () => {
  const { challengeActionPopupState, hideChallengeActionPopup } =
    useNotifications();
  const navigation = useNavigation();

  const handleExploreKallout = () => {
    navigation.dispatch(StackActions.replace(AppScreens.DrawerRoute));
  };

  const handleSetShowModal = (show: boolean) => {
    if (!show) {
      hideChallengeActionPopup();
    }
  };

  // Type guard to ensure we have the correct shape
  const action =
    "action" in challengeActionPopupState
      ? challengeActionPopupState.action
      : "reject";
  const autoRedirect =
    "autoRedirect" in challengeActionPopupState
      ? challengeActionPopupState.autoRedirect
      : false;

  return (
    <ChallengeActionPopup
      showModal={challengeActionPopupState.visible}
      setShowModal={handleSetShowModal}
      action={action}
      autoRedirectToHome={autoRedirect}
      redirectDelay={3000}
      onClose={hideChallengeActionPopup}
      onExploreKallout={handleExploreKallout}
    />
  );
};

const NotificationModals: React.FC<any> = ({
  currentNotification,
  loginUserData,
  showModal,
  setShowModal,
  showSmallModalForNewChallenge,
  setShowSmallModalForNewChallenge,
  showCustomCoinToast,
  setShowCustomCoinToast,
  customCoinToastText,
  customCoinToastDay,
  customCoinToastCoin,
  currentEventUpdate,
  previousEventDetails,
  showEventUpdateModal,
  setShowEventUpdateModal,
  currentEventStatusUpdate,
  showChangesApprovedModal,
  setShowChangesApprovedModal,
  showChangesRejectedModal,
  setShowChangesRejectedModal,
  showWinnerModal,
  setShowWinnerModal,
  eventDetails,
  newWinner,
  greatEffortPopup,
  setGreatEffortPopup,
  navigation,
  finalWarningPopUp,
  setFinalWarningPopUp,
  eventData,
  loginUserDataRef,
  showChallengeResponsePopup,
  setShowChallengeResponsePopup,
  challengeResponseNotification,
  showCancelledEventPopup,
  setShowCancelledEventPopup,
  cancelledEventNotification,
  showInfluencerApprovalPopup,
  setShowInfluencerApprovalPopup,
  influencerApprovalData,
  showNegotiationActivityPopup,
  setShowNegotiationActivityPopup,
  negotiationActivityData,
}) => {
  const deviceSize = useDeviceSize();
  const isMobile = deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

  // Helper function to extract notificationId from various possible paths
  const getNotificationId = (notification: any) => {
    return notification?.notificationData?.notificationId ||
      notification?.data?.notificationId ||
      notification?.data?.id ||
      notification?.notificationId ||
      notification?.id;
  };

  // Helper function to extract userId from various possible paths
  const getNotificationUserId = (notification: any) => {
    return notification?.notificationData?.userId ||
      notification?.data?.userId ||
      notification?.userId ||
      notification?.data?.senderId ||
      notification?.senderId;
  };

  return (
    <>
      {currentNotification && loginUserData && showModal && (
        isMobile ? (
          <MobileNotificationDetails
            showModal={showModal}
            setShowModal={setShowModal}
            challengeID={getNotificationId(currentNotification)}
            username={getNotificationUserId(currentNotification)}
            accessToken={loginUserData.AccessToken}
            onAcceptPress={() => { }}
          />
        ) : (
          <NotificationDetails
            showModal={showModal}
            setShowModal={setShowModal}
            challengeID={getNotificationId(currentNotification)}
            username={getNotificationUserId(currentNotification)}
            accessToken={loginUserData.AccessToken}
            onAcceptPress={() => { }}
          />
        )
      )}
      {currentNotification && loginUserData && (
        <ChallengeNotificationPopup
          visible={showSmallModalForNewChallenge}
          challengeID={getNotificationId(currentNotification)}
          username={getNotificationUserId(currentNotification)}
          accessToken={loginUserData.AccessToken}
          onAccept={() => { }}
          onClose={() => setShowSmallModalForNewChallenge(false)}
          onViewDetails={() => {
            setShowModal(true);
            setShowSmallModalForNewChallenge(false);
          }}
        />
      )}

      {showCustomCoinToast && (
        <CustomCoinToast
          isVisible={showCustomCoinToast}
          onClose={() => setShowCustomCoinToast(false)}
          customCoinToastText={customCoinToastText}
          customCoinToastDay={customCoinToastDay}
          customCoinToastCoin={customCoinToastCoin}
        />
      )}

      {currentEventUpdate && previousEventDetails && (
        <EventsDetailsUpdatedModal
          showModal={showEventUpdateModal}
          setShowModal={setShowEventUpdateModal}
          eventID={currentEventUpdate.eventId}
          onClose={() => setShowEventUpdateModal(false)}
          updatedEventDetails={currentEventUpdate.updatedEventDetails}
          previousEventDetails={previousEventDetails}
          notificationId={currentEventUpdate.notificationId}
          accessToken={loginUserDataRef.current?.AccessToken}
        />
      )}

      {currentEventStatusUpdate && (
        <>
          <ChangesApprovedModal
            showModal={showChangesApprovedModal}
            setShowModal={setShowChangesApprovedModal}
            eventID={currentEventStatusUpdate.eventId}
            onClose={() => setShowChangesApprovedModal(false)}
          />
          <ChangesRejectedModal
            showModal={showChangesRejectedModal}
            setShowModal={setShowChangesRejectedModal}
            eventID={currentEventStatusUpdate.eventId}
            onClose={() => setShowChangesRejectedModal(false)}
          />
        </>
      )}

      {showWinnerModal && (
        <WinnerModal
          closeWinnerModal={() => setShowWinnerModal(false)}
          openWinnerModal={showWinnerModal}
          eventDetails={eventDetails}
          winnerDetails={newWinner}
        />
      )}

      {greatEffortPopup && (
        <GreatEffortModal
          visible={greatEffortPopup}
          onClose={() => setGreatEffortPopup(false)}
          onExploreMoreEvents={() => {
            setGreatEffortPopup(false);
            navigateToScreen(navigation, AppScreens.DrawerRoute);
          }}
        />
      )}

      {finalWarningPopUp && (
        <FinalWarning
          eventData={eventData}
          setShowModal={setFinalWarningPopUp}
          showModal={finalWarningPopUp}
        />
      )}

      <GlobalChallengeActionPopup />

      {/* Challenge Response Popup (Accepted/Rejected) */}
      <ChallengeResponsePopup
        visible={showChallengeResponsePopup}
        onClose={() => setShowChallengeResponsePopup(false)}
        onViewEvent={() => setShowChallengeResponsePopup(false)}
        notificationData={challengeResponseNotification}
      />

      {/* Cancelled Event Popup */}
      {showCancelledEventPopup && cancelledEventNotification && (
        <View
          style={{
            position: "absolute",
            bottom: isMobile ? 70 : 16,
            right: isMobile ? 8 : 16,
            left: isMobile ? 8 : undefined,
            width: isMobile ? undefined : 400,
            backgroundColor: "#400000",
            borderRadius: 8,
            borderWidth: 2,
            borderColor: AppColors.koBrandRed,
            padding: isMobile ? 12 : 16,
            zIndex: 9999,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
            elevation: 10,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
              <Text
                style={[
                  isMobile ? AppFontStyle.BOLD_ANYBODY_12 : AppFontStyle.BOLD_ANYBODY_16,
                  { color: AppColors.koBrandRed },
                ]}
              >
                ⚠️ Event Cancelled
              </Text>
            </View>
            <TouchableOpacity onPress={() => setShowCancelledEventPopup(false)}>
              <Image
                source={AppImages.icClose}
                style={{ width: isMobile ? 14 : 20, height: isMobile ? 14 : 20, tintColor: "white" }}
                resizeMode="contain"
              />
            </TouchableOpacity>
          </View>

          <Text
            style={[
              isMobile ? AppFontStyle.REGULAR_ANYBODY_12 : AppFontStyle.REGULAR_ANYBODY_14,
              { color: "white", marginTop: 8 },
            ]}
          >
            {cancelledEventNotification?.data?.details?.cancelReason ||
              cancelledEventNotification?.data?.message ||
              "This event has been cancelled"}
          </Text>

          <Text
            style={[
              isMobile ? AppFontStyle.REGULAR_ANYBODY_10 : AppFontStyle.REGULAR_ANYBODY_12,
              { color: "#CCCCCC", marginTop: 4 },
            ]}
          >
            {cancelledEventNotification?.data?.details?.eventTitle}
          </Text>

          <TouchableOpacity
            onPress={() => setShowCancelledEventPopup(false)}
            style={{
              marginTop: 12,
              backgroundColor: AppColors.koBrandRed,
              borderRadius: 6,
              paddingVertical: isMobile ? 8 : 10,
              alignItems: "center",
            }}
          >
            <Text
              style={[
                isMobile ? AppFontStyle.BOLD_ANYBODY_12 : AppFontStyle.BOLD_ANYBODY_14,
                { color: "white" },
              ]}
            >
              Dismiss
            </Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Influencer Approval Popup */}
      {showInfluencerApprovalPopup && influencerApprovalData && (
        <View
          style={{
            position: "fixed" as any,
            bottom: isMobile ? 10 : 20,
            right: isMobile ? 10 : 20,
            borderRadius: 8,
            borderWidth: 1.6,
            borderColor: "#DC2626",
            padding: 12,
            gap: 8,
            maxWidth: isMobile ? 320 : 420,
            minWidth: isMobile ? 280 : 360,
            zIndex: 9999,
            overflow: "hidden",
          }}
        >
          {/* Gradient Background */}
          <LinearGradient
            colors={["#400000", "#291B1B"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />

          {/* Header Row */}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <View style={{ flexDirection: "row", alignItems: "center", gap: 10, flex: 1 }}>
              <Text style={{ fontSize: isMobile ? 28 : 36, color: "#DC2626", fontWeight: "900" }}>
                KO
              </Text>
              <Text
                style={{
                  color: "#00C002",
                  fontSize: isMobile ? 16 : 20,
                  fontWeight: "900",
                }}
              >
                {influencerApprovalData.isApproved ? "Congratulation" : "Application Rejected"}
              </Text>
            </View>
            <TouchableOpacity onPress={() => setShowInfluencerApprovalPopup(false)}>
              <Image
                source={AppImages.icClose}
                style={{ width: isMobile ? 20 : 24, height: isMobile ? 20 : 24, tintColor: "white" }}
                resizeMode="contain"
              />
            </TouchableOpacity>
          </View>

          {/* Main Text - Approved */}
          {influencerApprovalData.isApproved && (
            <>
              <Text
                style={{
                  color: "#FFF",
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: "600",
                  lineHeight: 24,
                }}
              >
                Your KO Beta access request has been approved.
              </Text>

              <Text
                style={{
                  color: "#FFF",
                  fontSize: isMobile ? 13 : 14,
                  fontWeight: "700",
                }}
              >
                Here's what you unlock:
              </Text>
              <View style={{ gap: 2 }}>
                <Text style={{ color: "#FFF", fontSize: isMobile ? 12 : 14, fontWeight: "400" }}>
                  Create and host events
                </Text>
                <Text style={{ color: "#FFF", fontSize: isMobile ? 12 : 14, fontWeight: "400" }}>
                  Stream directly inside KO Kallout
                </Text>
                <Text style={{ color: "#FFF", fontSize: isMobile ? 12 : 14, fontWeight: "400" }}>
                  Earn rewards and grow your influence
                </Text>
              </View>
            </>
          )}

          {/* Main Text - Rejected */}
          {!influencerApprovalData.isApproved && (
            <>
              <Text
                style={{
                  color: "#FFF",
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: "600",
                  lineHeight: 24,
                }}
              >
                Thank you for your interest in the KO Beta program as an influencer.
              </Text>

              <View style={{ gap: 4 }}>
                <Text style={{ color: "#FFF", fontSize: isMobile ? 12 : 14, fontWeight: "400" }}>
                  • Unfortunately, your access request has not been approved at this time.
                </Text>
                <Text style={{ color: "#FFF", fontSize: isMobile ? 12 : 14, fontWeight: "400" }}>
                  • We appreciate your enthusiasm and encourage you to reapply in the future as opportunities evolve.
                </Text>
              </View>
            </>
          )}
        </View>
      )}

      {/* Negotiation Activity Popup - Bottom Toast Style (like Challenge Declined) */}
      {showNegotiationActivityPopup && negotiationActivityData && (
        <View
          style={{
            position: "absolute" as any,
            bottom: isMobile ? 70 : 16,
            right: isMobile ? 8 : 16,
            left: isMobile ? 8 : undefined,
            width: isMobile ? undefined : 400,
            borderRadius: 8,
            zIndex: 9999,
          }}
          pointerEvents="box-none"
        >
          <LinearGradient
            colors={["#400000", "#291B1B"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={{
              borderRadius: 8,
              borderWidth: 2,
              borderColor: AppColors.koBrandRed,
              overflow: "hidden",
              padding: isMobile ? 12 : 16,
            }}
          >
            {/* Header */}
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                <Image
                  source={AppImages.icChallengeIcon}
                  style={{ width: isMobile ? 24 : 32, height: isMobile ? 24 : 32 }}
                  resizeMode="contain"
                />
                <Text
                  style={[
                    isMobile ? AppFontStyle.BOLD_ANYBODY_14 : AppFontStyle.BOLD_ANYBODY_18,
                    { color: AppColors.koBrandRed },
                  ]}
                >
                  Opponent wants negotiating
                </Text>
              </View>
              <TouchableOpacity onPress={() => setShowNegotiationActivityPopup(false)}>
                <Image
                  source={AppImages.icClose}
                  style={{ width: isMobile ? 16 : 20, height: isMobile ? 16 : 20, tintColor: "white" }}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            </View>

            {/* Message */}
            <Text
              style={[
                isMobile ? AppFontStyle.REGULAR_ANYBODY_12 : AppFontStyle.REGULAR_ANYBODY_14,
                { color: "white", marginTop: 12 },
              ]}
            >
              Your opponent <Text style={{ fontWeight: "bold" }}>@{negotiationActivityData?.userName || "Opponent"}</Text> started negotiating before accepting the challenge.
            </Text>

            {/* Event Title */}
            <Text
              style={[
                isMobile ? AppFontStyle.BOLD_ANYBODY_14 : AppFontStyle.BOLD_ANYBODY_16,
                { color: "white", marginTop: 12 },
              ]}
              numberOfLines={2}
            >
              {negotiationActivityData?.eventTitle || "Event"}
            </Text>

            {/* Date/Time */}
            <Text
              style={[
                isMobile ? AppFontStyle.REGULAR_ANYBODY_12 : AppFontStyle.REGULAR_ANYBODY_14,
                { color: "white", marginTop: 4 },
              ]}
            >
              {negotiationActivityData?.createdAt
                ? `${new Date(negotiationActivityData.createdAt).toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                })} ${new Date(negotiationActivityData.createdAt).toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })}`
                : ""}
            </Text>

            {/* Action Buttons - Side by Side */}
            <View style={{ flexDirection: "row", gap: 10, marginTop: 16 }}>
              {/* Dismiss Button */}
              <TouchableOpacity
                onPress={() => setShowNegotiationActivityPopup(false)}
                style={{ flex: 1 }}
              >
                <View
                  style={{
                    backgroundColor: "transparent",
                    borderWidth: 1,
                    borderColor: "white",
                    borderRadius: 8,
                    paddingVertical: isMobile ? 10 : 12,
                    alignItems: "center",
                  }}
                >
                  <Text
                    style={[
                      isMobile ? AppFontStyle.BOLD_ANYBODY_14 : AppFontStyle.BOLD_ANYBODY_16,
                      { color: "white" },
                    ]}
                  >
                    Dismiss
                  </Text>
                </View>
              </TouchableOpacity>

              {/* Open Chat Button */}
              <TouchableOpacity
                onPress={() => {
                  setShowNegotiationActivityPopup(false);
                  // Navigate to OnGoingEvents with Chat tab
                  if (negotiationActivityData?.eventId) {
                    navigateToScreen(navigation, AppScreens.OnGoingEvents, {
                      eventID: negotiationActivityData.eventId,
                      tab: "Chat",
                    });
                  }
                }}
                style={{ flex: 1 }}
              >
                <View
                  style={{
                    backgroundColor: AppColors.koBrandRed,
                    borderRadius: 8,
                    paddingVertical: isMobile ? 10 : 12,
                    alignItems: "center",
                  }}
                >
                  <Text
                    style={[
                      isMobile ? AppFontStyle.BOLD_ANYBODY_14 : AppFontStyle.BOLD_ANYBODY_16,
                      { color: "white" },
                    ]}
                  >
                    Open chat
                  </Text>
                </View>
              </TouchableOpacity>
            </View>
          </LinearGradient>
        </View>
      )}
    </>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    // Return safe defaults when used outside provider (e.g., for non-logged-in users)
    console.warn(
      "useNotifications called outside NotificationProvider - returning defaults"
    );
    return {
      notifications: [],
      eventNotifications: [],
      sendNotification: () => { },
      sendEventNotification: async () => { },
      setNotifications: () => { },
      showModal: false,
      newNotification: null,
      updateNotification: false,
      setUpdateNotification: () => { },
      newNotificationCount: 0,
      setNewNotificationCount: () => { },
      eventStatus: null,
      activityUpdate: null,
      signUpUser: [],
      checkInNotifications: [],
      checkInStatus: { organiser: false, challenger: false },
      refreshNotificationCounts: async () => { },
      notificationCounts: {
        organizer: 0,
        challenger: 0,
        audience: 0,
        total: 0,
      },
      isOnNotificationScreen: false,
      visitedNotificationTabs: new Set(),
      trackTabVisit: () => { },
      markNotificationsAsSeen: async () => { },
      showChallengeActionPopup: false,
      hideChallengeActionPopup: () => { },
      challengeActionPopupState: {
        visible: false,
        notificationData: null,
        onAccept: () => { },
        onReject: () => { },
      },
    };
  }
  return context;
};
